import { useContext, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, ComponentLoader, FormGroup, Heading, Input } from '@snsw-gel/react';
import FlowStateContext from 'app/contexts/FlowStateContext';
import { useEventFlow } from 'app/contexts/EventFlowContext';
import { getProfileConnectURL } from 'app/services/utils/url';
import { getStorageItem, setStorageItem } from 'app/sharedServices/storage/storage';
import { FormContainer, StyledBackButton, StyledRow } from './EventPersonalDetails.styled';
import EventLayout from './EventLayout';

interface EventPersonalDetailsProps {
  agencyName: string;
}

interface PersonalDetailsForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const TEXT_PATTERN = /^[a-zA-Z0-9!@#$&()\-_`.+,/ ']*$/;
const EMAIL_PATTERN =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const PHONE_PATTERN =
  /^\({0,1}((0|\+61)(2|4|3|7|8)){0,1}\){0,1}( |-){0,1}[0-9]{2}( |-){0,1}[0-9]{2}( |-){0,1}[0-9]{1}( |-){0,1}[0-9]{3}$/;

// ProfileConnectListener redirects back here with this referrer after a
// successful Profile Connect journey.
const PROFILE_CONNECT_SUCCESS_PATH = '/profile-connect/success';

const EventPersonalDetails: React.FC<EventPersonalDetailsProps> = ({ agencyName }) => {
  const history = useHistory();
  const location = useLocation<{ referrer?: string }>();
  const { formData } = useContext(FlowStateContext);
  const { personalDetails: savedDetails, setPersonalDetails, eventId } = useEventFlow();

  // The transaction type is read from session storage, where the landing page
  // stored it after loading the event details. This mirrors UserDetails, which
  // reads the same key that ServiceRouter stores for the service flow.
  const profileConnectTransactionType = getStorageItem('profileConnectTransactionType', 'session');

  const businessDetailsPath = `/events/${agencyName}/business-details`;
  const landingPath = `/events/${agencyName}/landing?eventCode=${eventId}`;

  // Whether the user has just returned to this page from a successful Profile
  // Connect journey. Used to avoid re-triggering the redirect (loop guard).
  const userCameFromProfileConnect = location.state?.referrer === PROFILE_CONNECT_SUCCESS_PATH;

  // Profile Connect is usable when we have a place to send the user (the
  // configured external URL) and a transaction type recorded for this event. In
  // offline My Account mode there is no real Profile Connect service to redirect
  // to, so we fall back to the manual form.
  const isFakeUser = process.env.REACT_APP_OFFLINE_MY_ACCOUNT === 'true';
  const isProfileConnectAvailable = !isFakeUser && !!process.env.PROFILE_CONNECT_URL && !!profileConnectTransactionType;

  // Hand off to Profile Connect when it is available and the user has not
  // already been through it (no saved details, not returning from success).
  // Otherwise render the manual form.
  const shouldRedirectToProfileConnect = isProfileConnectAvailable && !userCameFromProfileConnect;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonalDetailsForm>({
    defaultValues: savedDetails || {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
  });

  // On mount, either hand off to Profile Connect (when available) or, if the
  // user is returning from a Profile Connect success, pre-fill the form from
  // the profile data captured by ProfileConnectListener.
  useEffect(() => {
    if (shouldRedirectToProfileConnect) {
      // Record where Profile Connect should send the user back to on success
      // (business details, the next step) and on cancel (the landing page, the
      // step before this one). ProfileConnectListener reads these keys on
      // return. Cancel must NOT return to this page, or its mount logic would
      // immediately re-trigger the Profile Connect redirect and loop.
      setStorageItem('profileConnectRedirectPath', businessDetailsPath, 'session');
      setStorageItem('lastPath', landingPath, 'session');
      window.location.href = getProfileConnectURL(profileConnectTransactionType);
    }

    // Pre-fill from Profile Connect data when the user returned here after a
    // successful journey (or, defensively, whenever profile data exists and the
    // user has not already saved event-specific details).
    if (!savedDetails) {
      const profileDetails = formData?.personalDetails;
      if (profileDetails) {
        reset({
          firstName: profileDetails.firstName || '',
          lastName: profileDetails.lastName || '',
          email: profileDetails.email || '',
          phone: profileDetails.phone || '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.personalDetails]);

  const onSubmit = (data: PersonalDetailsForm) => {
    setPersonalDetails(data);
    history.push(businessDetailsPath);
  };

  const handleBack = () => {
    history.push(landingPath);
  };

  // While the browser is being redirected to Profile Connect, show a loader
  // rather than briefly flashing the manual form.
  if (shouldRedirectToProfileConnect) {
    return (
      <EventLayout currentStep={1}>
        <ComponentLoader label="Authenticating..." />
      </EventLayout>
    );
  }

  return (
    <EventLayout currentStep={1}>
      <Heading level={1}>Personal details</Heading>
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: 'First name is required',
              maxLength: { value: 50, message: 'First name should NOT be longer than 50 characters' },
              pattern: { value: TEXT_PATTERN, message: 'Invalid first name' },
            }}
            render={({ field }) => (
              <FormGroup
                id="firstName"
                label="Given name"
                errorMessage={errors.firstName?.message}
                hasError={!!errors.firstName}
              >
                <Input {...field} id="firstName" />
              </FormGroup>
            )}
          />

          <Controller
            name="lastName"
            control={control}
            rules={{
              required: 'Last name is required',
              maxLength: { value: 50, message: 'Last name should NOT be longer than 50 characters' },
              pattern: { value: TEXT_PATTERN, message: 'Invalid last name' },
            }}
            render={({ field }) => (
              <FormGroup
                id="lastName"
                label="Family name"
                errorMessage={errors.lastName?.message}
                hasError={!!errors.lastName}
              >
                <Input {...field} id="lastName" />
              </FormGroup>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Email address is required',
              maxLength: { value: 100, message: 'Email address should NOT be longer than 100 characters' },
              pattern: { value: EMAIL_PATTERN, message: 'Invalid email address' },
            }}
            render={({ field }) => (
              <FormGroup
                id="email"
                label="Email address"
                errorMessage={errors.email?.message}
                hasError={!!errors.email}
              >
                <Input {...field} id="email" type="email" />
              </FormGroup>
            )}
          />

          <Controller
            name="phone"
            control={control}
            rules={{
              required: 'Phone number is required',
              pattern: { value: PHONE_PATTERN, message: 'Invalid phone number' },
            }}
            render={({ field }) => (
              <FormGroup
                id="phone"
                label="Phone number (mobile preferred)"
                helpMessage="Enter a 10-digit number. Include the area code for landline numbers."
                errorMessage={errors.phone?.message}
                hasError={!!errors.phone}
              >
                <Input {...field} id="phone" type="tel" />
              </FormGroup>
            )}
          />

          <StyledRow>
            <Button type="submit">Continue</Button>
          </StyledRow>

          <StyledRow>
            <StyledBackButton variant="back" onClick={handleBack} type="button">
              Back
            </StyledBackButton>
          </StyledRow>
        </form>
      </FormContainer>
    </EventLayout>
  );
};

export default EventPersonalDetails;
